// index.jsx (PublicStatistics)
import React, { useState, useCallback, useEffect } from 'react';
import { Tab, Tabs } from 'react-bootstrap';
import ApplicationsTab from './ApplicationsTab';
import GroupsTab from './GroupsTab';
import ChildrenTab from './ChildrenTab';
import FiltersSection from './FiltersSection';
import { useIsMobile } from './hooks/useIsMobile';
import { statisticsApi } from './statisticsApi';
import { downloadTableXls, copyTableToClipboard } from '../../utils/exportTableExcell';
import './public-statistics.css';
import './public-statistics-mobile.css';

// ─── Хелперы для дефолтных значений ──────────────────────────────────────────
function getCurrentYearLabel() {
  const currentYear = new Date().getMonth() >= 8
    ? new Date().getFullYear()
    : new Date().getFullYear() - 1;
  return `${currentYear} → ${currentYear + 1}`;
}

function getCurrentMonthName() {
  const months = [
    'Вересень', 'Жовтень', 'Листопад', 'Грудень', 'Січень', 'Лютий',
    'Березень', 'Квітень', 'Травень', 'Червень', 'Липень', 'Серпень',
  ];
  const m = new Date().getMonth();
  return months[m < 8 ? m + 5 : m - 7] || 'Травень';
}

const DEFAULT_FILTERS = {
  applications: {
    year: getCurrentYearLabel(),
    registration: 'all',
    municipalCard: 'all',
    benefit: ['wo'],
    created: 'all',
    invitations: 'all',
    enrolled: 'all',
    moved: 'all',
  },
  groups: {
    year: getCurrentYearLabel(),
    district: ['all'],
    ageCategory: ['all'],
    placeType: ['all'],
    disabilityType: ['all'],
    internationalAgreement: 'all',
    benefit: ['wo'],
  },
  children: {
    year: getCurrentYearLabel(),
    registration: 'all',
    placeType: 'all',
    disabilityType: ['all'],
    benefit: ['wo'],
    asOf: getCurrentMonthName(),
  },
};

const PublicStatistics = () => {
  const [activeTab, setActiveTab] = useState('applications');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const isMobile = useIsMobile();

  // ── Черновые фильтры (то что пользователь меняет в UI) ─────────────────────
  const [filters, setFilters] = useState(DEFAULT_FILTERS);

  // ── Применённые фильтры (то что уходит в запросы) ──────────────────────────
  // Инициализируются теми же значениями — чтобы данные загрузились сразу.
  const [appliedFilters, setAppliedFilters] = useState(DEFAULT_FILTERS);

  // ── Справочники загружаются ОДИН РАЗ здесь и пробрасываются вниз ───────────
  const [refYears, setRefYears] = useState([]);
  const [refDistricts, setRefDistricts] = useState([]);
  const [refBenefits, setRefBenefits] = useState([]);
  const [refDisabilities, setRefDisabilities] = useState([]);
  const [refLoading, setRefLoading] = useState(false);

  useEffect(() => {
    setRefLoading(true);
    Promise.all([
      statisticsApi.getYearsAndDistricts(),
      statisticsApi.getBenefits(),
      statisticsApi.getDisabilities(),
    ])
      .then(([yearsRes, benefitsRes, disabilitiesRes]) => {
        if (yearsRes.data?.cursor) {
          const currentYear = new Date().getMonth() >= 8
            ? new Date().getFullYear()
            : new Date().getFullYear() - 1;
          setRefYears(
            yearsRes.data.cursor.map(item => ({
              value: item._id,
              label: `${item._id} → ${item._id + 1}`,
            }))
          );
          const cur = yearsRes.data.cursor.find(item => item._id === currentYear);
          if (cur) setRefDistricts(['all', ...cur.dist]);
        }
        if (benefitsRes.data && Array.isArray(benefitsRes.data)) {
          setRefBenefits([
            { _id: 'wo', name: 'Всі заявки' },
            { _id: 'all', name: 'Всі пільги' },
            ...benefitsRes.data,
          ]);
        }
        if (disabilitiesRes.data && Array.isArray(disabilitiesRes.data)) {
          setRefDisabilities(disabilitiesRes.data);
        }
      })
      .catch(e => console.error('Помилка завантаження довідників:', e))
      .finally(() => setRefLoading(false));
  }, []); // один раз

  // ── Изменение черновых фильтров ─────────────────────────────────────────────
  const handleFilterChange = useCallback((tab, filterName, value) => {
    setFilters(prev => ({
      ...prev,
      [tab]: { ...prev[tab], [filterName]: value },
    }));
  }, []);

  // ── Применение фильтров (кнопка «Відобразити») ─────────────────────────────
  const handleApplyFilters = useCallback((tab) => {
    setError(null);
    setAppliedFilters(prev => ({
      ...prev,
      [tab]: { ...filters[tab] },
    }));
  }, [filters]);

  // ── Экспорт ──────────────────────────────────────────────────────────────────
  const exportConfig = {
    applications: { tableId: 'pubstat-applications-table', title: 'Заявки на вступ' },
    groups:       { tableId: 'pubstat-groups-table',       title: 'Групи в садочках' },
    children:     { tableId: 'pubstat-children-table',     title: 'Діти в садочках'  },
  };

  const xlsOptions = {
    removeSelectors: ['button', '.action-btn', 'input', 'img', '.expand-toggle-btn'],
    stripedEvenColor: '#ffffff',
    stripedOddColor:  '#f8f9fa',
    theadColor:       '#cfe2ff',
  };

  const getExportTitle = useCallback(() => {
    const { title } = exportConfig[activeTab];
    const year = appliedFilters[activeTab]?.year || '';
    const date = new Date().toLocaleDateString('uk-UA');
    const asOf = activeTab === 'children' && appliedFilters.children?.asOf
      ? `. Станом на: ${appliedFilters.children.asOf}`
      : '';
    return `${title}. Навч. рік: ${year}${asOf}. Дата вивантаження: ${date}`;
  }, [activeTab, appliedFilters]);

  const getExportFilename = useCallback(() => {
    const year = (appliedFilters[activeTab]?.year || '').replace(/\s*→\s*/, '-');
    const date = new Date().toISOString().split('T')[0];
    return `pubstat_${activeTab}_${year}_${date}`;
  }, [activeTab, appliedFilters]);

  const handleDownloadXls = useCallback(() => {
    downloadTableXls(
      exportConfig[activeTab].tableId,
      getExportTitle(),
      getExportFilename(),
      xlsOptions
    );
  }, [activeTab, getExportTitle, getExportFilename]);

  const handleCopy = useCallback(() => {
    copyTableToClipboard(
      exportConfig[activeTab].tableId,
      getExportTitle(),
      xlsOptions
    );
  }, [activeTab, getExportTitle]);

  return (
    <main className="publicstatistic-main-content">
      <div className="container py-4">

        <div className="d-flex align-items-center justify-content-between mb-3 flex-wrap gap-2">
          <h1 className="publicstatistic-page-title mb-0">Статистична інформація</h1>
          <div className="btn-group btn-group-sm">
            <button
              className="btn btn-outline-secondary"
              onClick={handleCopy}
              title="Скопіювати таблицю для вставки в Excel (Ctrl+V)"
            >
              <i className="bi bi-clipboard-data"></i>
              {!isMobile && ' Копіювати'}
            </button>
            <button
              className="btn btn-outline-success"
              onClick={handleDownloadXls}
              title="Завантажити таблицю як Excel файл"
            >
              <i className="bi bi-file-earmark-excel"></i>
              {!isMobile && ' .xls'}
            </button>
          </div>
        </div>

        {error && (
          <div className="alert alert-danger alert-dismissible fade show" role="alert">
            <strong>Помилка!</strong> {error}
            <button type="button" className="btn-close" onClick={() => setError(null)} aria-label="Close"></button>
          </div>
        )}

        {loading && (
          <div className="text-center py-3">
            <div className="spinner-border text-primary" role="status">
              <span className="visually-hidden">Завантаження...</span>
            </div>
          </div>
        )}

        <div className="publicstatistic-statistics-tabs">
          {/*
            mountOnEnter  — монтирует таб только когда его открыли в первый раз.
            unmountOnExit — размонтирует при переходе на другой таб.
            Без этих флагов все три таба монтируются сразу и делают ~13 запросов
            при первом открытии страницы.
          */}
          <Tabs
            activeKey={activeTab}
            onSelect={(k) => {
              setActiveTab(k);
              setError(null);
            }}
            className={`publicstatistic-nav-tabs ${isMobile ? 'publicstatistic-nav-tabs-mobile' : ''}`}
            id="publicstatistic-statisticsTabs"
            mountOnEnter
            unmountOnExit
          >
            <Tab eventKey="applications" title="Заявки на вступ" tabClassName="publicstatistic-nav-link">
              <FiltersSection
                tab="applications"
                filters={filters.applications}
                onFilterChange={handleFilterChange}
                onApplyFilters={handleApplyFilters}
                years={refYears}
                districts={refDistricts}
                benefits={refBenefits}
                disabilities={refDisabilities}
                loadingRefs={refLoading}
              />
              <ApplicationsTab
                filters={appliedFilters.applications}
                onLoading={setLoading}
                onError={setError}
              />
            </Tab>

            <Tab eventKey="children" title="Діти в садочках" tabClassName="publicstatistic-nav-link">
              <FiltersSection
                tab="children"
                filters={filters.children}
                onFilterChange={handleFilterChange}
                onApplyFilters={handleApplyFilters}
                years={refYears}
                districts={refDistricts}
                benefits={refBenefits}
                disabilities={refDisabilities}
                loadingRefs={refLoading}
              />
              <ChildrenTab
                filters={appliedFilters.children}
                onLoading={setLoading}
                onError={setError}
              />
            </Tab>

            <Tab eventKey="groups" title="Групи в садочках" tabClassName="publicstatistic-nav-link">
              <FiltersSection
                tab="groups"
                filters={filters.groups}
                onFilterChange={handleFilterChange}
                onApplyFilters={handleApplyFilters}
                years={refYears}
                districts={refDistricts}
                benefits={refBenefits}
                disabilities={refDisabilities}
                loadingRefs={refLoading}
              />
              <GroupsTab
                filters={appliedFilters.groups}
                onLoading={setLoading}
                onError={setError}
              />
            </Tab>
          </Tabs>
        </div>
      </div>
    </main>
  );
};

export default PublicStatistics;